import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Filter, X, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { flagEmoji } from "@/lib/countries";
import Flag from "@/components/Flag";
import ForfaitProgress from "@/components/ForfaitProgress";
import MissionNotifications, { missionNotifKeys, missionNotifSeverityScore } from "@/components/MissionNotifications";
import RemindersPausedBadge from "@/components/RemindersPausedBadge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const STATUS_TABS = [
  { key: "ACTIVE", label: "Actives" },
  { key: "PENDING_VALIDATION", label: "En attente" },
  { key: "DRAFT", label: "Brouillons" },
  { key: "CLOSED", label: "Closed" },
  { key: "ALL", label: "Toutes" },
];

const ALL = "__ALL__";

export default function Missions() {
  const { user } = useAuth();
  const [missions, setMissions] = useState([]);
  const [clients, setClients] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  // Iter-54: CEO / ADMIN land on the "Toutes" tab by default so they see every mission of the
  // company at a glance. Other roles keep the legacy "Actives" default.
  const defaultTab = (user?.role === "CEO" || user?.role === "ADMIN") ? "ALL" : "ACTIVE";
  const activeTab = searchParams.get("status") || defaultTab;
  const [clientFilter, setClientFilter] = useState(ALL);
  const [typeFilter, setTypeFilter] = useState(ALL);
  const [currencyFilter, setCurrencyFilter] = useState(ALL);
  // Iter-113: filter rows by the kind of notification they have. Special values:
  //   "ANY" = at least one notif, "NONE" = no notif at all.
  const [notifFilter, setNotifFilter] = useState(ALL);
  // Iter-195n — toggle to include/exclude closed missions independently of
  // the status tab. Persisted in URL so the choice survives reloads and is
  // shareable. Defaults to "hidden" on non-CLOSED/ALL tabs so the list
  // stays focused on actionable rows; "shown" on CLOSED tab (obvious) and
  // on ALL tab when explicitly toggled.
  const includeClosedParam = searchParams.get("include_closed");
  const includeClosed = includeClosedParam === "1";
  const setIncludeClosed = (v) => {
    const sp = Object.fromEntries(searchParams.entries());
    if (v) sp.include_closed = "1"; else delete sp.include_closed;
    setSearchParams(sp);
  };
  // Iter-203b — tri-state filter on the Weekly CRA opt-in flag.
  //   "ALL"  = no filter
  //   "ON"   = only missions where weekly_cra_enabled is truthy
  //   "OFF"  = only missions where weekly_cra_enabled is falsy
  // Persisted in URL so the choice survives reloads / can be shared.
  const weeklyCraFilter = searchParams.get("weekly_cra") || ALL;
  const setWeeklyCraFilter = (v) => {
    const sp = Object.fromEntries(searchParams.entries());
    if (v === ALL) delete sp.weekly_cra; else sp.weekly_cra = v;
    setSearchParams(sp);
  };
  // Iter-105: sortable columns. `null` direction = unsorted (preserves backend default order).
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const toggleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir("asc"); return; }
    if (sortDir === "asc") { setSortDir("desc"); return; }
    // 3rd click on the same column → clear sort
    setSortKey(null); setSortDir("asc");
  };

  // Iter-195p — extracted into a callable so the RemindersPausedBadge can
  // refresh the list after re-enabling reminders on a mission.
  const load = () => {
    api.get("/missions").then((r) => setMissions(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data)).catch(() => setClients([]));
  }, []);

  const clientMap = useMemo(
    () => Object.fromEntries(clients.map((c) => [c.client_id, c])),
    [clients]
  );

  const filtered = useMemo(() => {
    let list = activeTab === "ALL" ? missions : missions.filter((m) => m.status === activeTab);
    // Iter-195n — globally hide closed missions unless either:
    //   • the user explicitly chose the CLOSED tab, or
    //   • the user toggled "Inclure clôturées" on (works on every tab).
    if (activeTab !== "CLOSED" && !includeClosed) {
      list = list.filter((m) => m.status !== "CLOSED");
    }
    if (clientFilter !== ALL) list = list.filter((m) => m.client_id === clientFilter);
    if (typeFilter !== ALL) list = list.filter((m) => m.type === typeFilter);
    if (currencyFilter !== ALL) list = list.filter((m) => m.currency === currencyFilter);
    // Iter-203b — Weekly CRA opt-in filter.
    if (weeklyCraFilter === "ON") list = list.filter((m) => !!m.weekly_cra_enabled);
    else if (weeklyCraFilter === "OFF") list = list.filter((m) => !m.weekly_cra_enabled);
    if (notifFilter !== ALL) {
      list = list.filter((m) => {
        const keys = missionNotifKeys(m);
        if (notifFilter === "ANY") return keys.length > 0;
        if (notifFilter === "NONE") return keys.length === 0;
        return keys.includes(notifFilter);
      });
    }
    if (sortKey) {
      const dir = sortDir === "asc" ? 1 : -1;
      const STATUS_ORDER = { ACTIVE: 0, PENDING_VALIDATION: 1, DRAFT: 2, CLOSED: 3 };
      const key = sortKey;
      list = [...list].sort((a, b) => {
        let av; let bv;
        if (key === "client") {
          av = (clientMap[a.client_id]?.name || "").toLowerCase();
          bv = (clientMap[b.client_id]?.name || "").toLowerCase();
        } else if (key === "title") {
          av = (a.title || "").toLowerCase(); bv = (b.title || "").toLowerCase();
        } else if (key === "type") {
          av = a.type || ""; bv = b.type || "";
        } else if (key === "budget_jh") {
          av = Number(a.budget_jh || 0); bv = Number(b.budget_jh || 0);
        } else if (key === "status") {
          av = STATUS_ORDER[a.status] ?? 99; bv = STATUS_ORDER[b.status] ?? 99;
        } else if (key === "currency") {
          av = a.currency || ""; bv = b.currency || "";
        } else if (key === "notifications") {
          // Iter-113: severity-based sort. Highest severity comes "first" in asc by
          // default — that's what users want (most actionable rows on top).
          av = -missionNotifSeverityScore(a);
          bv = -missionNotifSeverityScore(b);
        }
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    }
    return list;
  }, [missions, activeTab, includeClosed, clientFilter, typeFilter, currencyFilter, notifFilter, weeklyCraFilter, sortKey, sortDir, clientMap]);

  const hasActiveFilter = clientFilter !== ALL || typeFilter !== ALL || currencyFilter !== ALL || notifFilter !== ALL;
  // Iter-195n — count of closed missions in the dataset (used for the
  // "Inclure clôturées (N)" toggle label so the CEO knows how many archives
  // are hidden behind the toggle).
  const closedCount = useMemo(
    () => missions.filter((m) => m.status === "CLOSED").length,
    [missions]
  );
  // Iter-203b — total count of ACTIVE missions opted-in to Weekly CRA. Surfaced
  // inside the filter dropdown label so CEO/Manager see at a glance how many
  // missions are currently running the weekly cadence.
  const weeklyCraOnCount = useMemo(
    () => missions.filter((m) => m.weekly_cra_enabled && m.status === "ACTIVE").length,
    [missions]
  );
  const clearFilters = () => {
    setClientFilter(ALL); setTypeFilter(ALL); setCurrencyFilter(ALL); setNotifFilter(ALL);
    setWeeklyCraFilter(ALL);
  };

  // Distinct currencies actually present in the dataset (keeps the dropdown relevant)
  const currenciesPresent = useMemo(
    () => Array.from(new Set(missions.map((m) => m.currency).filter(Boolean))).sort(),
    [missions]
  );

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1400px]">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-brand-navy-500 mb-3 font-mono">Delivery</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Missions</h1>
        </div>
        <div className="inline-flex items-center gap-2">
          {user?.role === "CEO" && (
            <Link to="/simulations" data-testid="missions-go-simulations"
              className="inline-flex items-center gap-2 px-4 md:px-5 h-10 border border-zinc-200 text-zinc-800 text-sm rounded-md hover:bg-zinc-50">
              Simulations
            </Link>
          )}
          <Link to="/missions/new" data-testid="new-mission-btn" className="inline-flex items-center gap-2 px-4 md:px-5 h-10 bg-brand-navy text-white text-sm rounded-md hover:bg-brand-navy-600">
            <Plus size={14} /> Nouvelle mission
          </Link>
        </div>
      </div>

      {/* Status tabs */}
      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="missions-status-tabs">
        {STATUS_TABS.map((t) => {
          const count = t.key === "ALL" ? missions.length : missions.filter((m) => m.status === t.key).length;
          const isActive = activeTab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setSearchParams(t.key === defaultTab ? {} : { status: t.key })}
              data-testid={`missions-tab-${t.key}`}
              className={`px-3.5 h-9 rounded-full text-xs font-medium transition-colors inline-flex items-center gap-2 ${
                isActive
                  ? "bg-brand-navy text-white"
                  : "bg-white border border-brand-navy/15 text-brand-ink hover:bg-brand-orange-100 hover:text-brand-orange-600"
              }`}
            >
              {t.label}
              <span className={`text-[10px] font-mono ${isActive ? "text-brand-cream" : "text-zinc-500"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap items-center gap-3 mb-5 px-4 py-3 bg-white border border-brand-navy/15 rounded-lg" data-testid="missions-filters">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-brand-navy-500 font-mono">
          <Filter size={13} /> Filtres
        </div>
        <div className="w-60">
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger data-testid="filter-client" className="h-9"><SelectValue placeholder="Client" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All clients</SelectItem>
              {clients.map((c) => (
                <SelectItem key={c.client_id} value={c.client_id} data-testid={`filter-client-opt-${c.client_id}`}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="filter-type" className="h-9"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All types</SelectItem>
              <SelectItem value="REGIE" data-testid="filter-type-opt-REGIE">Regie</SelectItem>
              <SelectItem value="FORFAIT" data-testid="filter-type-opt-FORFAIT">Forfait</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-36">
          <Select value={currencyFilter} onValueChange={setCurrencyFilter}>
            <SelectTrigger data-testid="filter-currency" className="h-9"><SelectValue placeholder="Devise" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Toutes devises</SelectItem>
              {currenciesPresent.map((c) => (
                <SelectItem key={c} value={c} data-testid={`filter-currency-opt-${c}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-44">
          <Select value={notifFilter} onValueChange={setNotifFilter}>
            <SelectTrigger data-testid="filter-notifs" className="h-9"><SelectValue placeholder="Notifs" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Toutes notifs</SelectItem>
              <SelectItem value="ANY" data-testid="filter-notifs-opt-ANY">Avec notif…</SelectItem>
              <SelectItem value="po_missing" data-testid="filter-notifs-opt-po_missing">BC manquant</SelectItem>
              <SelectItem value="no_contract" data-testid="filter-notifs-opt-no_contract">Contrat absent</SelectItem>
              <SelectItem value="client_pending" data-testid="filter-notifs-opt-client_pending">Client pending</SelectItem>
              <SelectItem value="po_only" data-testid="filter-notifs-opt-po_only">BC seul</SelectItem>
              <SelectItem value="no_country" data-testid="filter-notifs-opt-no_country">Pays manquant</SelectItem>
              <SelectItem value="NONE" data-testid="filter-notifs-opt-NONE">Sans notif</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {/* Iter-203b — Weekly CRA opt-in quick filter (CEO/Manager). */}
        <div className="w-44">
          <Select value={weeklyCraFilter} onValueChange={setWeeklyCraFilter}>
            <SelectTrigger data-testid="filter-weekly-cra" className="h-9"><SelectValue placeholder="Weekly CRA" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tous (Weekly CRA)</SelectItem>
              <SelectItem value="ON" data-testid="filter-weekly-cra-opt-ON">
                Avec Weekly CRA ({weeklyCraOnCount})
              </SelectItem>
              <SelectItem value="OFF" data-testid="filter-weekly-cra-opt-OFF">
                Sans Weekly CRA
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        {/* Iter-195n — Toggle to show/hide closed missions independently of
            the status tab. Hidden when already on the CLOSED tab (where it
            would be redundant). */}
        {activeTab !== "CLOSED" && (
          <label
            data-testid="filter-include-closed"
            className="inline-flex items-center gap-2 px-3 h-9 text-xs text-zinc-700 cursor-pointer select-none border border-zinc-200 rounded-md hover:bg-zinc-50"
            title="Afficher aussi les missions clôturées (archives)"
          >
            <input
              type="checkbox"
              checked={includeClosed}
              onChange={(e) => setIncludeClosed(e.target.checked)}
              data-testid="filter-include-closed-checkbox"
              className="h-3.5 w-3.5 accent-brand-navy"
            />
            <span>Inclure clôturées</span>
            {closedCount > 0 && (
              <span className="font-mono text-[10px] text-zinc-500">({closedCount})</span>
            )}
          </label>
        )}
        {hasActiveFilter && (
          <button
            onClick={clearFilters}
            data-testid="filters-clear"
            className="inline-flex items-center gap-1.5 px-3 h-9 text-xs text-zinc-600 hover:text-brand-navy hover:bg-brand-cream-200 rounded-md"
          >
            <X size={13} /> Réinitialiser
          </button>
        )}
        <div className="ml-auto text-xs text-zinc-500 font-mono">
          {filtered.length} résultat{filtered.length > 1 ? "s" : ""}
        </div>
      </div>

      <div className="bg-white border border-brand-navy/15 rounded-lg overflow-hidden">
        <div className="hidden md:grid grid-cols-12 px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-brand-navy/10 font-mono">
          {[
            { key: "title", label: "Mission", span: "col-span-3", align: "left", sortable: true },
            { key: "client", label: "Client", span: "col-span-2", align: "left", sortable: true },
            { key: "type", label: "Type", span: "col-span-1", align: "left", sortable: true },
            { key: "budget_jh", label: "Budget JH", span: "col-span-1", align: "left", sortable: true },
            { key: "status", label: "Statut", span: "col-span-2", align: "left", sortable: true },
            { key: "notifications", label: "Notifs", span: "col-span-2", align: "left", sortable: true },
            { key: "currency", label: "Devise", span: "col-span-1", align: "right", sortable: true },
          ].map((col) => {
            const isActive = sortKey === col.key;
            const Icon = isActive ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
            return (
              <button
                key={col.key}
                onClick={() => toggleSort(col.key)}
                data-testid={`mission-sort-${col.key}`}
                title={isActive ? `Tri ${sortDir === "asc" ? "croissant" : "décroissant"} — cliquez pour ${sortDir === "asc" ? "inverser" : "désactiver"}` : `Trier par ${col.label}`}
                className={`${col.span} inline-flex items-center gap-1 ${col.align === "right" ? "justify-end" : ""} hover:text-brand-navy transition-colors text-left ${isActive ? "text-brand-navy" : ""}`}
              >
                <span>{col.label}</span>
                <Icon size={11} className={isActive ? "opacity-100" : "opacity-30"} />
              </button>
            );
          })}
        </div>
        {filtered.length === 0 && (
          <div className="px-5 py-12 text-center text-sm text-zinc-500" data-testid="missions-empty">
            {hasActiveFilter ? "No mission matches the filters." : (activeTab === "CLOSED" ? "No closed mission." : "No mission in this category.")}
          </div>
        )}
        {filtered.map((m) => {
          const statusColor = {
            ACTIVE: "bg-green-100 text-green-800",
            PENDING_VALIDATION: "bg-amber-100 text-amber-800",
            DRAFT: "bg-zinc-100 text-zinc-700",
            CLOSED: "bg-brand-orange-100 text-brand-orange-600",
          }[m.status] || "bg-zinc-100 text-zinc-700";
          const clientName = clientMap[m.client_id]?.name || "—";
          return (
            <Link
              key={m.mission_id}
              to={`/missions/${m.mission_id}`}
              data-testid={`mission-row-${m.mission_id}`}
              className="block md:grid md:grid-cols-12 px-4 md:px-5 py-3 md:py-4 md:items-center border-b border-brand-navy/10 hover:bg-brand-cream-50 transition-colors last:border-b-0"
            >
              {/* Mobile: stacked card layout */}
              <div className="md:col-span-3 flex items-start gap-3 min-w-0">
                <span className="w-2 h-2 rounded-full shrink-0 mt-1.5" style={{ background: m.color || "#355278" }} />
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">
                    <span className="truncate">{m.title}</span>
                    {/* Iter-203b — Weekly CRA opt-in pill, visible at a glance. */}
                    {m.weekly_cra_enabled && (
                      <span
                        data-testid={`mission-row-weekly-cra-${m.mission_id}`}
                        title="Cette mission est en flux CRA hebdomadaire"
                        className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200 align-middle"
                      >
                        WCRA
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-600 truncate md:hidden">{clientName}</div>
                  {/* Mobile: render notifications inline under the title */}
                  <div className="md:hidden mt-1.5">
                    <MissionNotifications mission={m} />
                  </div>
                  <div className="hidden md:block text-[10px] font-mono text-zinc-500 mt-0.5" data-testid="mission-country-badge">{m.country ? <span className="inline-flex items-center gap-1"><Flag code={m.country} size={14} alt={false} /> {m.country}</span> : ""}</div>
                  {/* Iter-185 — Forfait progress under mission title (both mobile + desktop) */}
                  <ForfaitProgress mission={m} compact />
                </div>
                {/* Mobile-only inline status pill */}
                <span className={`md:hidden inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono shrink-0 ${statusColor}`}>{m.status}</span>
              </div>
              <div className="hidden md:block md:col-span-2 text-sm text-zinc-600 truncate">{clientName}</div>
              <div className="hidden md:block md:col-span-1 text-xs font-mono text-zinc-500">{m.type}</div>
              <div className="hidden md:block md:col-span-1 text-sm font-mono">{m.type === "FORFAIT" ? <span className="text-zinc-400">—</span> : m.budget_jh}</div>
              <div className="hidden md:block md:col-span-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono ${statusColor}`}>
                  {m.status}
                </span>
              </div>
              <div className="hidden md:block md:col-span-2" data-testid={`mission-notifs-${m.mission_id}`}>
                <MissionNotifications mission={m} />
                {/* Iter-195p/s — pause badge inline with other mission badges */}
                <RemindersPausedBadge
                  row={m}
                  kind="mission"
                  id={m.mission_id}
                  canReactivate={user?.role === "CEO"}
                  allowInitialPause={user?.role === "CEO"}
                  onChanged={load}
                />
              </div>
              <div className="hidden md:block md:col-span-1 text-right text-xs font-mono text-zinc-500">{m.currency}</div>
              {/* Mobile-only secondary line */}
              <div className="md:hidden flex items-center gap-3 pl-5 mt-1.5 text-[11px] font-mono text-zinc-500">
                <span>{m.type}</span>
                {m.type !== "FORFAIT" && (
                  <>
                    <span>·</span>
                    <span>{m.budget_jh} JH</span>
                  </>
                )}
                <span>·</span>
                <span>{m.currency}</span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
